import { api } from '../../fetchWrapper';
import { logger } from '../../../utils/logger';
import { API_ENDPOINTS, getEndpoint } from '../../apiConstants';
import type { 
  StepData, 
  StepCreateRequest, 
  StepUpdateRequest, 
  StepApiResponse,
  StepSettingData,
  StepSettingCreateRequest,
  StepSettingUpdateRequest,
  StepRequestValidatorCreateRequest,
  StepRequestValidatorUpdateRequest
} from './jobDetail.types';

export interface JobDetailParams {
  clientCode: string;
  firstResult: number;
  maxResult: number;
  clientId?: number | string;
  jobName?: string;
  status?: number;
}

export interface JobDetailCreatePayload {
  jobName: string;
  jobCode: string;
  clientId: number;
  timeIntervalType: string;
  intervalType: string;
  status: string;
  nextRunTime: string;
  retryEnabled: boolean;
  isAutoRetryEnabled: boolean;
}

// Job detail API response format: array of arrays
// [jobId, interval, jobName, lastStart, lastEnd, nextRun, status, ...]
export type JobDetailResponse = Array<[
  number,    // jobId
  number,    // interval
  string,    // jobName
  number,    // lastStart (timestamp)
  number,    // lastEnd (timestamp)  
  number,    // nextRun (timestamp)
  number,    // status (0=inactive, 1=active)
  number,    // additional fields...
  number,
  boolean
]>;

export const fetchJobDetails = async (params: JobDetailParams): Promise<JobDetailResponse> => {
  const endpoint = getEndpoint(params.clientCode, API_ENDPOINTS.JOB.DETAIL_LIST);
  
  const queryParams = new URLSearchParams({
    firstResult: params.firstResult.toString(),
    maxResult: params.maxResult.toString(),
    searched: 'true', // Add searched=true parameter
  });
  
  if (params.clientId) {
    queryParams.append('clientId', params.clientId.toString());
  }
  
  if (params.jobName) {
    queryParams.append('jobName', params.jobName);
  }
  
  if (typeof params.status === 'number') {
    queryParams.append('status', params.status.toString());
  }
  
  const url = `${endpoint}?${queryParams.toString()}`;
  
  try {
    const response = await api.get<JobDetailResponse>(url);
    return Array.isArray(response) ? response : [];
  } catch (error) {
    logger.error('Error fetching job details:', error);
    throw error;
  }
};

// Create a new job detail
export const createJobDetail = async (params: {
  clientCode: string;
  payload: JobDetailCreatePayload;
}): Promise<any> => {
  // Backend accepts POST on base jobDetailRest/ for creation
  const endpoint = getEndpoint(params.clientCode, 'jobDetailRest/');
  try {
    const response = await api.post(endpoint, params.payload);
    return response;
  } catch (error) {
    logger.error('Error creating job detail:', error);
    throw error;
  }
};

export const fetchJobDetailsCount = async (params: Omit<JobDetailParams, 'firstResult' | 'maxResult'>): Promise<number> => {
  const endpoint = getEndpoint(params.clientCode, API_ENDPOINTS.JOB.DETAIL_COUNT);
  
  const queryParams = new URLSearchParams();
  
  // Add searched=true parameter as mentioned by the user
  queryParams.append('searched', 'true');
  
  if (params.clientId) {
    queryParams.append('clientId', params.clientId.toString());
  }
  
  if (params.jobName) {
    queryParams.append('jobName', params.jobName);
  }
  
  if (typeof params.status === 'number') {
    queryParams.append('status', params.status.toString());
  }
  
  const url = `${endpoint}?${queryParams.toString()}`;
  
  try {
    const response = await api.get(url);
    return typeof response === 'number' ? response : 0;
  } catch (error) {
    logger.error('Error fetching job details count:', error);
    return 0;
  }
};

// Get jobs for a specific client (for dropdown usage)
export const fetchJobsForClient = async (clientId: number | string): Promise<JobDetailResponse> => {
  return fetchJobDetails({
    clientCode: 'admin',
    firstResult: -1,
    maxResult: 0,
    clientId,
  });
};

// Get single job detail by ID
export const fetchJobDetailById = async (clientCode: string, jobId: number): Promise<JobDetailResponse[0] | null> => {
  try {
    const response = await fetchJobDetails({
      clientCode,
      firstResult: 0,
      maxResult: 1,
    });
    
    const job = response.find(jobArray => jobArray[0] === jobId);
    return job || null;
  } catch (error) {
    logger.error('Error fetching job detail by ID:', error);
    return null;
  }
};

// Get job names for dropdown (legacy compatibility)
// Supports both:
// - fetchJobNames({ clientCode, clientId? })
// - fetchJobNames(clientId)
export const fetchJobNames = async (params?: any): Promise<string[]> => {
  try {
    let clientId: number | string | undefined;
    let clientCode: string = 'admin';

    if (typeof params === 'object' && params !== null) {
      clientId = params.clientId;
      if (typeof params.clientCode === 'string' && params.clientCode.trim()) {
        clientCode = params.clientCode.trim();
      }
    } else if (typeof params === 'number' || typeof params === 'string') {
      clientId = params;
    }

    const response = await fetchJobDetails({
      clientCode,
      firstResult: -1,
      maxResult: 0,
      clientId,
    });

    return response.map((jobArray) => jobArray[2]); // jobName is at index 2
  } catch (error) {
    logger.error('Error fetching job names:', error);
    return [];
  }
};

// ============================================================================
// STEP API FUNCTIONS
// ============================================================================

// Step API response format: array of arrays (28 total arrays)
export type StepResponse = Array<any[]>;

// Fetch steps with pagination and filtering
export const fetchSteps = async (params: {
  clientCode: string;
  firstResult?: number;
  maxResult?: number;
  id?: number;
  clientId?: number;
  // Prefer jobDetailId; keep jobId for backward compatibility
  jobDetailId?: number;
  jobId?: number;
  name?: string;
  status?: string;
  searched?: boolean;
}): Promise<StepResponse> => {
  const endpoint = getEndpoint(params.clientCode, API_ENDPOINTS.JOB.STEP_LIST);
  
  const queryParams = new URLSearchParams();
  
  if (params.firstResult !== undefined) {
    queryParams.append('firstResult', params.firstResult.toString());
  }
  
  if (params.maxResult !== undefined) {
    queryParams.append('maxResult', params.maxResult.toString());
  }
  
  if (params.id !== undefined) {
    queryParams.append('id', params.id.toString());
  }
  
  if (params.clientId !== undefined) {
    queryParams.append('clientId', params.clientId.toString());
  }
  
  // Backend expects jobDetailId; support older callers using jobId
  const jobDetailId = params.jobDetailId ?? params.jobId;
  if (jobDetailId !== undefined) {
    queryParams.append('jobDetailId', jobDetailId.toString());
  }
  
  if (params.name) {
    queryParams.append('name', params.name);
  }
  
  if (params.status) {
    queryParams.append('status', params.status);
  }
  
  if (params.searched !== undefined) {
    queryParams.append('searched', params.searched.toString());
  }
  
  const url = `${endpoint}?${queryParams.toString()}`;
  
  try {
    const response = await api.get<StepResponse>(url);
    return Array.isArray(response) ? response : [];
  } catch (error) {
    logger.error('Error fetching steps:', error);
    throw error;
  }
};

// Fetch step enums (returns array of arrays with 3 total arrays)
export const fetchStepEnums = async (params: {
  clientCode: string;
  firstResult?: number;
  maxResult?: number;
}): Promise<Array<any[]>> => {
  const endpoint = getEndpoint(params.clientCode, API_ENDPOINTS.JOB.STEP_ENUMS);
  
  const queryParams = new URLSearchParams();
  
  if (params.firstResult !== undefined) {
    queryParams.append('firstResult', params.firstResult.toString());
  }
  
  if (params.maxResult !== undefined) {
    queryParams.append('maxResult', params.maxResult.toString());
  }
  
  const url = `${endpoint}?${queryParams.toString()}`;
  
  try {
    const response = await api.get<Array<any[]>>(url);
    return Array.isArray(response) ? response : [];
  } catch (error) {
    logger.error('Error fetching step enums:', error);
    throw error;
  }
};

// ============================================================================
// STEP REQUEST VALIDATOR API FUNCTIONS
// ============================================================================

// Step Request Validator API response format
export interface StepRequestValidatorResponse {
  createdBy: string | null;
  creationDate: number;
  lastUpdateDate: number;
  lastUpdatedBy: string | null;
  clientId: number;
  id: number;
  stepId: number;
  requestValidation: string;
  sequence: number;
  resultFlag: string;
}

// Fetch step request validators with pagination and filtering
export const fetchStepRequestValidators = async (params: {
  clientCode: string;
  firstResult?: number;
  maxResult?: number;
  stepId?: number;
  clientId?: number;
  searched?: boolean;
}): Promise<StepRequestValidatorResponse[]> => {
  const endpoint = getEndpoint(params.clientCode, API_ENDPOINTS.JOB.STEP_REQUEST_VALIDATOR_LIST);
  
  const queryParams = new URLSearchParams();
  
  if (params.firstResult !== undefined) {
    queryParams.append('firstResult', params.firstResult.toString());
  }
  
  if (params.maxResult !== undefined) {
    queryParams.append('maxResult', params.maxResult.toString());
  }
  
  if (params.stepId !== undefined) {
    queryParams.append('stepId', params.stepId.toString());
  }
  
  if (params.clientId !== undefined) {
    queryParams.append('clientId', params.clientId.toString());
  }
  
  if (params.searched !== undefined) {
    queryParams.append('searched', params.searched.toString());
  }
  
  const url = `${endpoint}?${queryParams.toString()}`;
  
  try {
    const response = await api.get<StepRequestValidatorResponse[]>(url);
    return Array.isArray(response) ? response : [];
  } catch (error) {
    logger.error('Error fetching step request validators:', error);
    throw error;
  }
};

// Fetch step request validator enums
export const fetchStepRequestValidatorEnums = async (params: {
  clientCode: string;
  firstResult?: number;
  maxResult?: number;
}): Promise<any[]> => {
  const endpoint = getEndpoint(params.clientCode, API_ENDPOINTS.JOB.STEP_REQUEST_VALIDATOR_ENUMS);
  
  const queryParams = new URLSearchParams();
  
  if (params.firstResult !== undefined) {
    queryParams.append('firstResult', params.firstResult.toString());
  }
  
  if (params.maxResult !== undefined) {
    queryParams.append('maxResult', params.maxResult.toString());
  }
  
  const url = `${endpoint}?${queryParams.toString()}`;
  
  try {
    const response = await api.get<any[]>(url);
    return Array.isArray(response) ? response : [];
  } catch (error) {
    logger.error('Error fetching step request validator enums:', error);
    throw error;
  }
};

// ============================================================================
// STEP CRUD API FUNCTIONS
// ============================================================================

// Keys allowed on step create; rest are edit-only. Include optional only when user entered a value to avoid 400.
const STEP_CREATE_REQUIRED_KEYS = ['subType', 'sequence', 'type', 'stepType', 'result', 'clientId', 'loggingEnabled', 'jobDetailId'] as const;
const STEP_CREATE_OPTIONAL_KEYS = ['successKey', 'successValue', 'path', 'requestTemplate', 'responseType', 'objectType', 'headerTemplate', 'responseFormatterJs', 'errorFormatterJs', 'requestType', 'stepCode', 'parentId', 'failureJobDetailId', 'requestFormatterJs', 'beforeAgentCall', 'autoRetryEnabled', 'maxAutoRetryCount'] as const;

function buildStepCreatePayload(stepData: StepCreateRequest): Record<string, unknown> {
  const raw = stepData as unknown as Record<string, unknown>;
  const payload: Record<string, unknown> = {};
  for (const key of STEP_CREATE_REQUIRED_KEYS) {
    if (raw[key] !== undefined) payload[key] = raw[key];
  }
  for (const key of STEP_CREATE_OPTIONAL_KEYS) {
    const v = raw[key];
    if (v === undefined) continue;
    if (v === null || v === '') continue;
    if (typeof v === 'string' && v.trim() === '') continue;
    payload[key] = v;
  }
  return payload;
}

// Create a new step (sends only create-allowed keys; optional keys included only when value entered)
export const createStep = async (params: {
  clientCode: string;
  stepData: StepCreateRequest;
}): Promise<StepApiResponse> => {
  const endpoint = getEndpoint(params.clientCode, API_ENDPOINTS.JOB.STEP_CREATE);
  const createPayload = buildStepCreatePayload(params.stepData);

  try {
    // Backend expects camelCase payload for create; do not transform keys
    const response = await api.post<StepApiResponse>(endpoint, createPayload);
    return response;
  } catch (error) {
    logger.error('Error creating step:', error);
    throw error;
  }
};

// Update an existing step (backend expects camelCase payload)
export const updateStep = async (params: {
  clientCode: string;
  stepData: StepUpdateRequest;
}): Promise<StepApiResponse> => {
  const endpoint = getEndpoint(params.clientCode, API_ENDPOINTS.JOB.STEP_UPDATE);

  try {
    const response = await api.put<StepApiResponse>(endpoint, params.stepData);
    return response;
  } catch (error) {
    logger.error('Error updating step:', error);
    throw error;
  }
};

// ============================================================================
// STEP SETTINGS CRUD API FUNCTIONS
// ============================================================================

// Create a new step setting
export const createStepSetting = async (params: {
  clientCode: string;
  settingData: StepSettingCreateRequest;
}): Promise<StepSettingData> => {
  const endpoint = getEndpoint(params.clientCode, API_ENDPOINTS.JOB.STEP_SETTING_CREATE);
  
  try {
    const response = await api.post<StepSettingData>(endpoint, params.settingData);
    return response;
  } catch (error) {
    logger.error('Error creating step setting:', error);
    throw error;
  }
};

// Update an existing step setting
export const updateStepSetting = async (params: {
  clientCode: string;
  settingData: StepSettingUpdateRequest;
}): Promise<StepSettingData> => {
  const endpoint = getEndpoint(params.clientCode, API_ENDPOINTS.JOB.STEP_SETTING_UPDATE);
  
  try {
    const response = await api.put<StepSettingData>(endpoint, params.settingData);
    return response;
  } catch (error) {
    logger.error('Error updating step setting:', error);
    throw error;
  }
};

// Delete a step setting by id
export const deleteStepSetting = async (params: {
  clientCode: string;
  id: number;
}): Promise<void> => {
  const endpoint = getEndpoint(params.clientCode, `${API_ENDPOINTS.JOB.STEP_SETTING_DELETE}${params.id}`);
  await api.delete(endpoint);
};

// ============================================================================
// STEP REQUEST VALIDATOR CRUD API FUNCTIONS
// ============================================================================

// Create a new step request validator
export const createStepRequestValidator = async (params: {
  clientCode: string;
  validatorData: StepRequestValidatorCreateRequest;
}): Promise<StepRequestValidatorResponse> => {
  const endpoint = getEndpoint(params.clientCode, API_ENDPOINTS.JOB.STEP_REQUEST_VALIDATOR_CREATE);
  
  try {
    const response = await api.post<StepRequestValidatorResponse>(endpoint, params.validatorData);
    return response;
  } catch (error) {
    logger.error('Error creating step request validator:', error);
    throw error;
  }
};

// Update an existing step request validator
export const updateStepRequestValidator = async (params: {
  clientCode: string;
  validatorData: StepRequestValidatorUpdateRequest;
}): Promise<StepRequestValidatorResponse> => {
  const endpoint = getEndpoint(params.clientCode, API_ENDPOINTS.JOB.STEP_REQUEST_VALIDATOR_UPDATE);
  
  try {
    const response = await api.put<StepRequestValidatorResponse>(endpoint, params.validatorData);
    return response;
  } catch (error) {
    logger.error('Error updating step request validator:', error);
    throw error;
  }
};

// Delete a step request validator by id
export const deleteStepRequestValidator = async (params: {
  clientCode: string;
  id: number;
}): Promise<void> => {
  const endpoint = getEndpoint(params.clientCode, `${API_ENDPOINTS.JOB.STEP_REQUEST_VALIDATOR_DELETE}${params.id}`);
  await api.delete(endpoint);
};